import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PhoneCall, MessageSquare, CalendarCheck, ShieldCheck, Clock, TrendingUp,
  PhoneMissed, Check, X, Plug, Bot, BarChart3, ArrowRight, Sparkles, PlayCircle,
  Quote, Star, Handshake, Users,
} from "lucide-react";
import { branding } from "@/config/branding";
import { API_BASE } from "@/lib/env";
import { Button, StatusBadge } from "@/components/ds";

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

const CALENDLY_URL = "https://calendly.com/claytondrewholdings/15min";
const CALENDLY_CSS = "https://assets.calendly.com/assets/external/widget.css";
const CALENDLY_JS = "https://assets.calendly.com/assets/external/widget.js";

declare global {
  interface Window { Calendly?: { initPopupWidget: (opts: { url: string }) => void } }
}

function openCalendly(e?: React.MouseEvent) {
  e?.preventDefault();
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url: CALENDLY_URL });
  } else {
    window.open(CALENDLY_URL, "_blank", "noopener");
  }
}

const REVIEWS = [
  { quote: "I called several companies and most never called me back.", source: "Angi Review" },
  { quote: "I left messages and still couldn't get anyone to respond.", source: "Google Review" },
  { quote: "I had to keep chasing them just to get an update.", source: "Google Review" },
  { quote: "The technician was good, but the communication from the company wasn't.", source: "Google Review" },
  { quote: "They scheduled an appointment, but no one showed up or called.", source: "Google Review" },
];

const PILLARS = [
  { icon: PhoneCall, title: "Answer", tone: "text-blue-600", bg: "bg-blue-50",
    items: ["AI voice receptionist", "SMS conversations", "After-hours answering", "Approved knowledge base"] },
  { icon: CalendarCheck, title: "Convert", tone: "text-emerald-600", bg: "bg-emerald-50",
    items: ["Lead qualification", "Appointment booking", "Lead recovery", "Estimate follow-up", "No-show recovery"] },
  { icon: TrendingUp, title: "Grow", tone: "text-violet-600", bg: "bg-violet-50",
    items: ["Customer reactivation", "Automated reviews", "Maintenance plans", "Seasonal campaigns", "Outbound AI calling"] },
  { icon: BarChart3, title: "Optimize", tone: "text-amber-600", bg: "bg-amber-50",
    items: ["Revenue analytics", "Lost-lead intelligence", "Smart dispatch", "AI Quality Coach", "Call-volume forecasts", "Optimization recommendations"] },
];

const FEATURES = [
  { icon: PhoneCall, title: "Answer every call, 24/7", body: "Your AI front desk picks up when you can't — day, night, weekends and holidays." },
  { icon: MessageSquare, title: "Reply to texts instantly", body: "Inbound SMS handled with approved answers, so leads never wait." },
  { icon: CalendarCheck, title: "Book real appointments", body: "Checks genuine availability and schedules jobs straight onto your calendar." },
  { icon: ShieldCheck, title: "Stays on script", body: "Only answers from approved knowledge — never invents prices or promises." },
  { icon: Clock, title: "Capture after-hours leads", body: "Turn missed calls into booked jobs instead of voicemails no one returns." },
  { icon: TrendingUp, title: "See the opportunities you capture", body: "A Missed Revenue dashboard shows calls answered, leads recovered, appointments booked and estimated opportunity value." },
  { icon: Users, title: "Bring past customers back automatically", body: "Launch reactivation campaigns — seasonal tune-ups, maintenance and inspections — to win repeat work from customers you already know." },
  { icon: PhoneCall, title: "Outbound AI calls (Pro)", body: "Queue AI callbacks for lead recovery, appointment confirmations, estimate follow-ups and reactivation — placed only when your voice provider is connected." },
  { icon: CalendarCheck, title: "Keep your business in front of customers throughout the year", body: "Plan seasonal campaigns — AC tune-ups, winterization, freeze prep, storm inspections — with templates, a calendar and recurring automation. Nothing sends without your authorization." },
];

const STEPS = [
  { icon: Plug, title: "1 · Connect in minutes", body: "Add your services, prices and hours. Point your number to ResFil. No engineers, no new hardware." },
  { icon: Bot, title: "2 · The AI answers & books", body: "It greets callers, answers questions from your approved knowledge, qualifies the lead and books the job on your calendar." },
  { icon: BarChart3, title: "3 · You see the results", body: "Every call, text, lead and booking in one inbox — with a weekly summary of the revenue you recovered." },
];

const WITHOUT = [
  "Calls ring out to voicemail after hours and during jobs",
  "Leads text and wait — then book with the competitor who replied first",
  "You pay $3,000+/mo for a receptionist who works 40 hours, not 168",
  "No record of why callers didn't book",
  "Every missed call is a silent, untracked loss",
];
const WITH = [
  "Every call answered in one ring — 24/7/365",
  "Texts answered instantly with your approved pricing and info",
  "A full-time front desk for a fraction of one salary",
  "Every call summarized with outcome and follow-ups",
  "A weekly report of booked jobs and recovered revenue",
];

const FAQ = [
  { q: "Will it sound like a robot?", a: "No. Callers hear a natural, friendly voice that greets them by your business name, answers their questions and books them in — most people don't realize it's AI." },
  { q: "What if it doesn't know an answer?", a: "It only speaks from the knowledge and prices you approve, and it never invents details. Anything outside scope is escalated to you or a message is taken — nothing is faked." },
  { q: "Do I need any tech skills?", a: "None. Setup takes minutes: add your services and hours, point your number, and you're live. No apps to install, no team to train." },
  { q: "How is this cheaper than a receptionist?", a: "One receptionist costs $3,000+/month and covers ~40 hours a week. ResFil covers all 168 hours — nights, weekends and holidays — from $299/month." },
];

export default function Landing() {
  const [calls, setCalls] = useState(15);
  const [value, setValue] = useState(450);
  const [close, setClose] = useState(40);

  useEffect(() => {
    if (!document.querySelector(`link[href="${CALENDLY_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CALENDLY_CSS;
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${CALENDLY_JS}"]`)) {
      const script = document.createElement("script");
      script.src = CALENDLY_JS;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const roi = useMemo(() => {
    const perMonth = Math.round(calls * 4.33);
    const atRiskJobs = perMonth * (close / 100);
    const lostRev = atRiskJobs * value;
    const recoveredJobs = Math.round(atRiskJobs * 0.6); // conservative share recovered by simply answering
    const recoveredRev = recoveredJobs * value;
    const price = 299;
    const multiple = recoveredRev > 0 ? recoveredRev / price : 0;
    return { perMonth, lostRev, recoveredJobs, recoveredRev, multiple };
  }, [calls, value, close]);

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-blue-100/50 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24">
          <div className="max-w-3xl">
            <StatusBadge tone="blue">AI Front Desk + Revenue &amp; Customer Operations Platform</StatusBadge>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              You Rest. <span className="text-blue-600">We Fill The Job.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {branding.productName} is the AI Front Desk &amp; Revenue Operations platform for home-service businesses —
              it <b>answers</b> every call and text, <b>converts</b> more leads into booked jobs, helps you <b>grow</b> repeat
              work, and shows you where to <b>optimize</b>. Built for HVAC, plumbing, electrical and roofing companies.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={openCalendly} data-testid="hero-register-btn">Book Demo</Button>
              <Link to="/pricing" data-testid="hero-pricing-btn"><Button size="lg" variant="outline">See pricing</Button></Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Live in minutes · No new hardware · Cancel anytime</p>
            <p className="mt-6 max-w-xl rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 text-sm font-medium text-blue-800" data-testid="hero-optimization-line">
              {branding.productName} doesn't just operate your AI front desk — it helps show you where your business can improve.
            </p>
          </div>
        </div>
      </section>

      {/* FOUR VALUE PILLARS */}
      <section className="border-b border-slate-200 bg-white" data-testid="pillars">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">One platform across the whole customer journey</h2>
          <p className="mt-2 max-w-2xl text-slate-600">{branding.productName} goes far beyond answering the phone — it helps you answer, convert, grow and optimize every opportunity.</p>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map(({ icon: Icon, title, tone, bg, items }) => (
              <div key={title} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`pillar-${title.toLowerCase()}`}>
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}><Icon className={`h-5 w-5 ${tone}`} strokeWidth={2} /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{title}</h3>
                <ul className="mt-3 space-y-1.5">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {it}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-400">Advanced operations, revenue intelligence and optimization features are included on higher plans. Outbound calling and telephony features activate once your voice/SMS provider is connected.</p>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="landing-multilingual">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">Multilingual AI</p>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Speak your customer's language</h2>
              <p className="mt-3 max-w-lg text-slate-600">ResFil automatically recognizes when customers communicate in another supported language and continues the conversation naturally — across calls, text messages, and website chat.</p>
              <p className="mt-3 text-sm font-medium text-slate-500">No menu. No restart. No unnecessary transfer.</p>
              <p className="mt-1 text-xs text-slate-400">English, Spanish, and additional supported languages based on plan and provider availability.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <div className="space-y-3 text-sm">
                <div className="flex justify-end"><span className="rounded-2xl rounded-br-sm bg-slate-200 px-4 py-2 text-slate-700">¿Habla español?</span></div>
                <div className="flex justify-start"><span className="rounded-2xl rounded-bl-sm bg-blue-600 px-4 py-2 text-white">Claro. ¿En qué puedo ayudarle hoy?</span></div>
                <div className="flex justify-end"><span className="rounded-2xl rounded-br-sm bg-slate-200 px-4 py-2 text-slate-700">Necesito reparar mi aire acondicionado.</span></div>
                <div className="flex justify-start"><span className="rounded-2xl rounded-bl-sm bg-blue-600 px-4 py-2 text-white">Con gusto. ¿La unidad está encendida en este momento?</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FEATURES */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Everything a great receptionist does</h2>
        <p className="mt-2 max-w-2xl text-slate-600">Operational, calm and reliable — built for busy owners, not tech teams.</p>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50"><Icon className="h-5 w-5 text-blue-600" strokeWidth={2} /></div>
              <h3 className="mt-4 font-medium text-slate-900">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 grid max-w-2xl grid-cols-3 gap-6" data-testid="hero-stats">
          {[["62%", "of calls to small businesses go unanswered"], ["85%", "of callers who hit voicemail never call back"], ["24/7", "your front desk never sleeps"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl font-semibold text-slate-900 sm:text-4xl">{n}</div>
              <div className="mt-1 text-xs leading-snug text-slate-500">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-600"><PlayCircle className="h-4 w-4" /> See &amp; hear it in action</div>
          <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Hear {branding.productName} answer a real call</h2>
          <p className="mt-2 max-w-2xl text-slate-600">Listen to a caller being greeted, answered and booked — start to finish — in under two minutes.</p>
          {/* The recording is square — the call audio over a still logo — so the frame stays square and
              centred rather than cropped into a widescreen box. Nothing downloads until it is played.
              It comes from the backend, not this folder: the platform's static server sends .mp4 as
              application/octet-stream with nosniff, which stops the browser playing it. The poster
              beside it is an image, which that server does serve correctly. */}
          <div
            className="mx-auto mt-8 w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-lg"
            data-testid="demo-video-slot"
          >
            <video
              className="aspect-square w-full"
              src={`${API_BASE}/api/public/media/resfil-demo.mp4`}
              poster="/demo/resfil-demo-poster.jpg"
              controls
              preload="none"
              playsInline
              data-testid="demo-video"
            >
              Your browser cannot play this recording.
            </video>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-slate-800 bg-slate-900">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex items-center gap-2 text-sm font-medium text-red-400"><PhoneMissed className="h-4 w-4" /> The problem</div>
          <h2 className="mt-3 max-w-3xl font-display text-2xl font-semibold text-white sm:text-3xl">
            Every unanswered call is a job your competitor just booked.
          </h2>
          <p className="mt-3 max-w-2xl text-slate-400">
            When you're on a job, driving, or asleep, the phone still rings — and most callers won't leave a voicemail.
            They call the next company on the list. That lost revenue never shows up on a report, so it's easy to miss just how much it adds up to.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[["$1,200+", "Average value of a single home-service job"], ["78%", "of customers hire the first business that responds"], ["27%", "of a typical week is outside 9–5 — when leads still call"]].map(([n, l]) => (
              <div key={l} className="rounded-xl border border-slate-800 bg-slate-800/50 p-5">
                <div className="font-display text-3xl font-semibold text-white">{n}</div>
                <div className="mt-1 text-sm text-slate-400">{l}</div>
              </div>
            ))}
          </div>

          {/* CUSTOMER FEEDBACK */}
          <div className="mt-16" data-testid="customer-feedback">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-400"><Quote className="h-4 w-4" /> Customer Feedback</div>
            <h3 className="mt-3 font-display text-xl font-semibold text-white sm:text-2xl">Real-world customer issues in your industry</h3>
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {REVIEWS.map((r, i) => (
                <figure key={i} data-testid={`review-${i}`}
                  className="flex flex-col justify-between rounded-xl border border-slate-800 bg-slate-800/50 p-5 transition-colors hover:border-slate-700">
                  <div>
                    <div className="flex gap-0.5 text-amber-400">
                      {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="h-3.5 w-3.5 fill-current" />)}
                    </div>
                    <blockquote className="mt-3 text-sm leading-relaxed text-slate-200">"{r.quote}"</blockquote>
                  </div>
                  <figcaption className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">{r.source}</figcaption>
                </figure>
              ))}
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border border-red-900/50 bg-red-950/30 p-5" data-testid="feedback-impact">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-300"><PhoneMissed className="h-4 w-4" /> Why this hurts your business</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Slow or missed responses don't just lose one job — they cost you the review, the referral and every repeat visit that customer would have brought.
                  Frustrated callers move straight to a competitor and often warn others in public reviews, quietly draining both your current revenue and your future pipeline.
                </p>
              </div>
              <div className="rounded-xl border border-emerald-900/50 bg-emerald-950/30 p-5" data-testid="feedback-solution">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-300"><Check className="h-4 w-4" /> How {branding.productName} fixes it</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  {branding.productName} answers every call and text instantly, 24/7 — no missed messages, no callbacks forgotten.
                  It keeps customers informed, books real appointments and follows up automatically, so every lead feels looked after and turns into a five-star experience instead of a bad review.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex items-center gap-2 text-sm font-medium text-blue-600"><Sparkles className="h-4 w-4" /> See your numbers</div>
          <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">How much are missed calls costing you?</h2>
          <p className="mt-2 max-w-2xl text-slate-600">Move the numbers to match your business. This is revenue that's slipping away every month.</p>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              {[
                { label: "Missed / unanswered calls per week", val: calls, set: setCalls, min: 1, max: 60, step: 1, fmt: (v: number) => `${v}` },
                { label: "Average value of a job", val: value, set: setValue, min: 100, max: 5000, step: 50, fmt: (v: number) => money(v) },
                { label: "How often a lead becomes a job (close rate)", val: close, set: setClose, min: 10, max: 90, step: 5, fmt: (v: number) => `${v}%` },
              ].map((r) => (
                <div key={r.label}>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">{r.label}</label>
                    <span className="font-display text-lg font-semibold text-blue-600">{r.fmt(r.val)}</span>
                  </div>
                  <input type="range" min={r.min} max={r.max} step={r.step} value={r.val}
                    onChange={(e) => r.set(Number(e.target.value))}
                    data-testid={`roi-${r.label.split(" ")[0].toLowerCase()}`}
                    className="mt-3 w-full accent-blue-600" />
                </div>
              ))}
              <p className="text-xs text-slate-400">Estimate only, for illustration. Assumes ResFil recovers a conservative 60% of otherwise-missed opportunities by answering instantly, 24/7.</p>
            </div>

            <div className="flex flex-col justify-between rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-600 to-blue-700 p-6 text-white shadow-lg" data-testid="roi-result">
              <div>
                <div className="text-sm text-blue-100">You're leaving roughly</div>
                <div className="font-display text-5xl font-bold tracking-tight" data-testid="roi-lost">{money(roi.lostRev)}<span className="text-2xl font-medium text-blue-200">/mo</span></div>
                <div className="mt-1 text-sm text-blue-100">on the table from ~{roi.perMonth} missed calls a month.</div>
              </div>
              <div className="mt-6 rounded-xl bg-white/10 p-4 backdrop-blur">
                <div className="text-sm text-blue-100">With {branding.productName} answering every one, you could recover about</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-bold" data-testid="roi-recovered">{money(roi.recoveredRev)}/mo</span>
                  <span className="text-sm text-blue-200">≈ {roi.recoveredJobs} extra booked jobs</span>
                </div>
                <div className="mt-3 flex items-center gap-2 text-emerald-200">
                  <TrendingUp className="h-5 w-5" />
                  <span className="font-semibold" data-testid="roi-multiple">That's {roi.multiple >= 1 ? `${roi.multiple.toFixed(0)}×` : "a strong"} return on your $299/mo plan</span>
                </div>
              </div>
              <Link to="/pricing" className="mt-6"><Button size="lg" className="w-full bg-white text-blue-700 hover:bg-blue-50" data-testid="roi-cta">Start capturing these calls <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* WITHOUT vs WITH */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">The difference one month makes</h2>
        <p className="mt-2 max-w-2xl text-slate-600">Same phone number. Completely different outcome.</p>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-semibold uppercase tracking-wide text-slate-400">Without {branding.productName}</div>
            <ul className="mt-4 space-y-3">
              {WITHOUT.map((t) => (
                <li key={t} className="flex gap-3 text-sm text-slate-600"><X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-blue-200 bg-blue-50/40 p-6 shadow-sm">
            <div className="text-sm font-semibold uppercase tracking-wide text-blue-600">With {branding.productName}</div>
            <ul className="mt-4 space-y-3">
              {WITH.map((t) => (
                <li key={t} className="flex gap-3 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Live in minutes, not months</h2>
          <p className="mt-2 max-w-2xl text-slate-600">No engineers. No new hardware. No script-writing.</p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white"><Icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Questions owners ask us</h2>
          <div className="mt-8 space-y-4">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="rounded-xl border border-slate-200 bg-white p-5" data-testid="faq-item">
                <h3 className="font-medium text-slate-900">{q}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AGENCY PARTNER PROGRAM (concise — full details on /partners) */}
      <section className="border-t border-slate-200 bg-white" data-testid="landing-partner-section">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="rounded-3xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-8 sm:p-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-700">
              <Handshake className="h-4 w-4" /> Agency Partner Program
            </div>
            <h2 className="mt-5 max-w-3xl font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Already Help Home-Service Businesses Grow? Partner With ResFil.
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600">
              The ResFil Agency Partner Program allows marketing agencies serving HVAC, plumbing, roofing, and electrical businesses to introduce their clients to ResFil and earn recurring commissions from eligible referrals.
            </p>

            <div className="mt-8 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center" data-testid="partner-value-flow">
              {["You Generate the Lead", "ResFil Helps Capture the Opportunity", "Your Client Gets the Job"].map((step, i) => (
                <React.Fragment key={step}>
                  <div className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-800">{step}</div>
                  {i < 2 && <ArrowRight className="mx-auto hidden h-4 w-4 shrink-0 text-blue-500 sm:block" />}
                </React.Fragment>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to="/partners" data-testid="partner-explore-btn"><Button size="lg">Explore the Agency Partner Program <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              <Link to="/partners#partner-form-section" className="text-sm font-medium text-blue-600 hover:text-blue-700" data-testid="partner-getinfo-link">Get More Info</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-slate-900">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-4 py-16 sm:flex-row sm:items-center sm:px-6">
          <div>
            <h2 className="font-display text-2xl font-semibold text-white sm:text-3xl">Stop paying for calls you never answer.</h2>
            <p className="mt-2 text-slate-400">Turn missed calls into booked jobs starting today — set up in minutes.</p>
          </div>
          <div className="flex gap-3">
            <Button size="lg" onClick={openCalendly} data-testid="final-register-btn">Book Demo</Button>
            <Link to="/pricing"><Button size="lg" variant="outline" className="border-slate-600 bg-transparent text-white hover:bg-slate-800" data-testid="final-pricing-btn">View pricing</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
